import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

export default function FeatureGraph({ features }) {
    const svgRef = useRef(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [filterLevel, setFilterLevel] = useState('all'); // all, features-only, high-impact
    const [searchTerm, setSearchTerm] = useState('');
    const [hoveredNode, setHoveredNode] = useState(null);

    useEffect(() => {
        if (!features.length) return;

        // Clear previous graph
        d3.select(svgRef.current).selectAll('*').remove();

        // Prepare data with filtering
        const { nodes, links } = prepareGraphData(features, filterLevel);
        
        // Set up dimensions
        const width = 1400;
        const height = 900;
        
        // Create SVG with zoom capabilities
        const svg = d3.select(svgRef.current)
            .attr('width', width)
            .attr('height', height);

        // Add zoom container
        const g = svg.append('g');
        
        // Add zoom behavior
        const zoom = d3.zoom()
            .scaleExtent([0.1, 4])
            .on('zoom', (event) => {
                g.attr('transform', event.transform);
            });
        
        svg.call(zoom);

        // Create force simulation with better spacing
        const simulation = d3.forceSimulation(nodes)
            .force('link', d3.forceLink(links)
                .id(d => d.id)
                .distance(d => {
                    // Variable distance based on connection type
                    if (d.source.type === 'feature' && d.target.type === 'feature') return 200;
                    if (d.source.type === 'feature' || d.target.type === 'feature') return 150;
                    return 100;
                }))
            .force('charge', d3.forceManyBody()
                .strength(d => d.type === 'feature' ? -500 : -200))
            .force('center', d3.forceCenter(width / 2, height / 2))
            .force('collision', d3.forceCollide().radius(d => d.type === 'feature' ? 40 : 20));

        // Create links
        const link = g.append('g')
            .selectAll('line')
            .data(links)
            .enter().append('line')
            .attr('stroke', d => {
                if (d.type === 'direct') return '#3B82F6';
                if (d.type === 'indirect') return '#9CA3AF';
                return '#E5E7EB';
            })
            .attr('stroke-width', d => d.type === 'direct' ? 2 : 1)
            .attr('stroke-dasharray', d => d.type === 'indirect' ? '5,5' : '')
            .attr('opacity', 0.6);

        // Create node groups
        const node = g.append('g')
            .selectAll('g')
            .data(nodes)
            .enter().append('g')
            .attr('cursor', 'pointer')
            .call(d3.drag()
                .on('start', dragstarted)
                .on('drag', dragged)
                .on('end', dragended));

        // Add circles with different sizes
        node.append('circle')
            .attr('r', d => {
                if (d.type === 'feature') return 20;
                if (d.connectionCount > 5) return 15;
                return 10;
            })
            .attr('fill', d => {
                if (d.type === 'table') {
                    // Color based on connection count (heat map)
                    const scale = d3.scaleSequential(d3.interpolateReds)
                        .domain([0, 10]);
                    return scale(d.connectionCount || 0);
                }
                // Feature colors by priority
                const colors = {
                    'Critical': '#EF4444',
                    'High': '#F97316',
                    'Medium': '#F59E0B',
                    'Low': '#10B981',
                    'Unknown': '#6366F1'
                };
                return colors[d.priority] || '#6366F1';
            })
            .attr('stroke', '#fff')
            .attr('stroke-width', 2);

        // Add labels with better positioning
        const labels = node.append('text')
            .text(d => d.name)
            .attr('font-size', d => d.type === 'feature' ? '12px' : '10px')
            .attr('font-weight', d => d.type === 'feature' ? 'bold' : 'normal')
            .attr('dx', 25)
            .attr('dy', 5)
            .style('pointer-events', 'none');

        // Add tooltips
        const tooltip = d3.select('body').append('div')
            .attr('class', 'tooltip')
            .style('opacity', 0)
            .style('position', 'absolute')
            .style('background', 'rgba(0, 0, 0, 0.8)')
            .style('color', 'white')
            .style('padding', '10px')
            .style('border-radius', '5px')
            .style('font-size', '12px')
            .style('pointer-events', 'none');

        // Node interactions
        node.on('mouseover', function(event, d) {
            setHoveredNode(d);
            
            // Show tooltip
            tooltip.transition().duration(200).style('opacity', .9);
            tooltip.html(getTooltipContent(d))
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 28) + 'px');
            
            // Highlight connected nodes
            const connectedNodeIds = new Set();
            links.forEach(link => {
                if (link.source.id === d.id) connectedNodeIds.add(link.target.id);
                if (link.target.id === d.id) connectedNodeIds.add(link.source.id);
            });
            
            node.style('opacity', n => {
                if (n.id === d.id) return 1;
                if (connectedNodeIds.has(n.id)) return 0.8;
                return 0.2;
            });
            
            link.style('opacity', l => {
                if (l.source.id === d.id || l.target.id === d.id) return 1;
                return 0.1;
            });
        })
        .on('mouseout', function() {
            setHoveredNode(null);
            tooltip.transition().duration(500).style('opacity', 0);
            node.style('opacity', 1);
            link.style('opacity', 0.6);
        })
        .on('click', function(event, d) {
            setSelectedNode(d);
            event.stopPropagation();
        });

        // Click on background to deselect
        svg.on('click', () => setSelectedNode(null));

        // Update positions on simulation tick
        simulation.on('tick', () => {
            link
                .attr('x1', d => d.source.x)
                .attr('y1', d => d.source.y)
                .attr('x2', d => d.target.x)
                .attr('y2', d => d.target.y);

            node.attr('transform', d => `translate(${d.x},${d.y})`);
        });

        // Search highlighting
        if (searchTerm) {
            node.style('opacity', d => {
                if (d.name.toLowerCase().includes(searchTerm.toLowerCase())) return 1;
                return 0.2;
            });
        }

        // Cleanup
        return () => {
            tooltip.remove();
        };

        // Drag functions
        function dragstarted(event) {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            event.subject.fx = event.subject.x;
            event.subject.fy = event.subject.y;
        }

        function dragged(event) {
            event.subject.fx = event.x;
            event.subject.fy = event.y;
        }

        function dragended(event) {
            if (!event.active) simulation.alphaTarget(0);
            event.subject.fx = null;
            event.subject.fy = null;
        }

    }, [features, filterLevel, searchTerm]);

    // Prepare filtered graph data
    function prepareGraphData(features, filterLevel) {
        const nodes = [];
        const links = [];
        const nodeMap = new Map();
        const tableConnectionCount = new Map();

        // First pass: count connections for each table
        features.forEach(feature => {
            const allTables = [
                ...(feature.tables?.direct || []),
                ...(feature.tables?.indirect || []),
                ...(feature.tables?.joins || [])
            ];
            allTables.forEach(table => {
                tableConnectionCount.set(table, (tableConnectionCount.get(table) || 0) + 1);
            });
        });

        // Add feature nodes
        features.forEach(feature => {
            const featureNode = {
                id: feature.id,
                name: feature.name,
                type: 'feature',
                category: feature.category,
                priority: feature.priority || 'Unknown',
                tableCount: [
                    ...(feature.tables?.direct || []),
                    ...(feature.tables?.indirect || []),
                    ...(feature.tables?.joins || [])
                ].length
            };
            
            nodes.push(featureNode);
            nodeMap.set(feature.id, featureNode);

            // Process tables based on filter level
            if (filterLevel !== 'features-only') {
                const allTables = [
                    ...(feature.tables?.direct || []),
                    ...(feature.tables?.indirect || []),
                    ...(feature.tables?.joins || [])
                ];

                allTables.forEach(table => {
                    const connectionCount = tableConnectionCount.get(table) || 0;
                    
                    // Filter by impact level
                    if (filterLevel === 'high-impact' && connectionCount < 3) {
                        return; // Skip low-impact tables
                    }

                    if (!nodeMap.has(`table_${table}`)) {
                        const tableNode = {
                            id: `table_${table}`,
                            name: table,
                            type: 'table',
                            connectionCount: connectionCount
                        };
                        nodes.push(tableNode);
                        nodeMap.set(`table_${table}`, tableNode);
                    }

                    // Add link
                    links.push({
                        source: feature.id,
                        target: `table_${table}`,
                        type: feature.tables.direct?.includes(table) ? 'direct' : 'indirect'
                    });
                });
            }
        });

        // Add feature-to-feature relationships
        if (filterLevel === 'features-only' || filterLevel === 'all') {
            features.forEach(feature => {
                feature.related_features?.forEach(related => {
                    if (nodeMap.has(related.feature_id)) {
                        links.push({
                            source: feature.id,
                            target: related.feature_id,
                            type: 'related',
                            shared_tables: related.shared_tables
                        });
                    }
                });
            });
        }

        return { nodes, links };
    }

    function getTooltipContent(node) {
        if (node.type === 'feature') {
            return `
                <strong>${node.name}</strong><br/>
                Category: ${node.category || 'Unknown'}<br/>
                Priority: ${node.priority}<br/>
                Tables: ${node.tableCount}
            `;
        } else {
            return `
                <strong>${node.name}</strong><br/>
                Type: Database Table<br/>
                Used by: ${node.connectionCount} features
            `;
        }
    }

    return (
        <div className="bg-white rounded-lg shadow p-4">
            {/* Controls */}
            <div className="mb-4 flex flex-wrap gap-4 items-center">
                <div>
                    <label className="text-sm font-medium mr-2">Filter:</label>
                    <select 
                        value={filterLevel} 
                        onChange={(e) => setFilterLevel(e.target.value)}
                        className="px-3 py-1 border rounded"
                    >
                        <option value="all">All Connections</option>
                        <option value="high-impact">High Impact Only (3+ connections)</option>
                        <option value="features-only">Features Only</option>
                    </select>
                </div>
                
                <div>
                    <input
                        type="text"
                        placeholder="Search nodes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="px-3 py-1 border rounded"
                    />
                </div>

                <div className="flex gap-2 text-sm">
                    <span className="flex items-center">
                        <span className="w-4 h-4 bg-blue-500 rounded-full mr-1"></span>
                        Feature
                    </span>
                    <span className="flex items-center">
                        <span className="w-4 h-4 bg-red-400 rounded-full mr-1"></span>
                        High-Use Table
                    </span>
                    <span className="flex items-center">
                        <span className="w-4 h-4 bg-gray-400 rounded-full mr-1"></span>
                        Table
                    </span>
                </div>
            </div>

            {/* Graph */}
            <div className="border rounded" style={{ height: '900px', overflow: 'hidden' }}>
                <svg ref={svgRef}></svg>
            </div>

            {/* Selected Node Details */}
            {selectedNode && (
                <div className="mt-4 p-4 bg-gray-50 rounded">
                    <h4 className="font-semibold">{selectedNode.name}</h4>
                    <p className="text-sm text-gray-600">
                        Type: {selectedNode.type === 'feature' ? 'Feature' : 'Database Table'}
                    </p>
                    {selectedNode.type === 'feature' && (
                        <>
                            <p className="text-sm">Category: {selectedNode.category}</p>
                            <p className="text-sm">Priority: {selectedNode.priority}</p>
                        </>
                    )}
                    {selectedNode.type === 'table' && (
                        <p className="text-sm">Connected Features: {selectedNode.connectionCount}</p>
                    )}
                </div>
            )}
        </div>
    );
}